/**
 * Rewrites attachment links in markdown to use decoded basenames only
 * 
 * Converts: ![image](Folder%20Name.assets/Image%20(2).png)
 * To:       ![image](Image (2).png)
 * 
 * Also populates empty alt text with filename for better rendering:
 * Converts: ![](path/to/image.png)
 * To:       ![image.png](image.png)
 * 
 * This ensures attachment references match the actual filenames uploaded to storage,
 * which is critical for Supabase where the editor matches attachments by filename.
 */

import { parseMarkdownLinks } from './parseMarkdownLinks'

export type AttachmentRewriteOptions = {
  basenameResolver?: (resolved: string) => string | undefined
}

/**
 * Rewrites markdown attachment links to use only decoded basenames
 * 
 * - Removes directory paths (including .assets folders)
 * - Removes URL encoding (%20 → space, etc.)
 * - Preserves image markers (!) and alt text
 * - Leaves external links unchanged
 * 
 * @param content - The markdown content to rewrite
 * @param notePath - The path of the current note (for relative path resolution)
 * @param options - Optional configuration including basenameResolver for custom name resolution
 * @returns Content with attachment links rewritten to basenames only
 */
export function rewriteAttachmentLinks(
  content: string,
  notePath: string,
  options?: AttachmentRewriteOptions
): string {
  const links = parseMarkdownLinks(content, notePath)
  
  // Filter to only attachment links
  const attachmentLinks = links.filter(link => link.kind === 'attachment')
  
  if (attachmentLinks.length === 0) {
    return content
  }
  
  // Sort links in reverse order by position to avoid offset issues during replacement
  const sortedLinks = attachmentLinks.sort((a, b) => {
    const aIndex = content.indexOf(a.raw)
    const bIndex = content.indexOf(b.raw)
    return bIndex - aIndex
  })
  
  let updatedContent = content
  
  for (const link of sortedLinks) {
    // Extract basename from the decoded resolved path
    // link.resolved is already decoded by parseMarkdownLinks (line 139)
    // Defensively strip angle brackets in case they slipped through
    let resolved = link.resolved
    if (resolved.startsWith('<') && resolved.endsWith('>')) {
      resolved = resolved.slice(1, -1).trim()
    }

    // Use resolver if provided, otherwise fall back to basename extraction
    const override = options?.basenameResolver?.(resolved)
    const fallback = resolved.split('/').pop() ?? resolved
    const basename = override ?? fallback
    
    // Populate empty alt text with filename for better rendering
    // Escape special characters that could break markdown syntax
    const alt = (link.text.trim() || basename)
      .replaceAll('[', String.raw`\[`)
      .replaceAll(']', String.raw`\]`)
    
    // Force attachment marker for all attachments (images and files)
    // so BlockNote processes them as inline attachments and resolves via storage
    const marker = '!'
    const replacement = `${marker}[${alt}](${basename})`
    
    // Replace this occurrence in the content
    const linkIndex = updatedContent.indexOf(link.raw)
    if (linkIndex !== -1) {
      updatedContent = 
        updatedContent.slice(0, linkIndex) + 
        replacement + 
        updatedContent.slice(linkIndex + link.raw.length)
    }
  }
  
  return updatedContent
}

